// ============================================
// pages/gunny-bags/GunnyRequestsManagement.tsx
// DM / MP / AO_CAO review queue: see society requests, adjust qty, approve/reject,
// and bundle approved requests into a Purchase Order.
// ============================================
import React, { useEffect, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle, X, Pencil, Package } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { gunnyRequestsAPI, gunnySuppliersAPI, gunnySupplierRatesAPI, seasonsAPI, commoditiesAPI } from '../../api/services';
import { UserRole } from '../../types/markfed';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';
import { bagTypeLabel } from '../../components/gunny/bagTypes';

interface ReqItem {
  id: number;
  bag_type: string;
  specification: string | null;
  requested_bags: number;
  dm_approved_bags: number | null;
  commodity_id?: number | null;
  commodity?: { id: number; name: string } | null;
}

interface Req {
  id: number;
  season_id: number;
  pacs_id: number;
  district_id: number;
  commodity_id: number | null;
  commodity?: { id: number; name: string; category?: string } | null;
  bag_type: string;
  specification: string | null;
  requested_bags: number;
  dm_approved_bags: number | null;
  required_by_date: string | null;
  delivery_godown_id: number | null;
  delivery_godown?: { id: number; godown_name: string; godown_type?: string };
  status: string;
  rejection_reason: string | null;
  notes: string | null;
  po_id: number | null;
  pacs?: { id: number; name: string; type: string };
  district?: { id: number; name: string };
  po?: { id: number; po_number: string; status: string };
  items?: ReqItem[];
}

export const GunnyRequestsManagement: React.FC = () => {
  const { hasRole } = useAuth();
  const canManage = hasRole(UserRole.DM, UserRole.AO_CAO, UserRole.MANAGER_PROCUREMENT);
  const [seasonId, setSeasonId] = useState<number | null>(null);
  const [seasons, setSeasons] = useState<any[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [reqs, setReqs] = useState<Req[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [editing, setEditing] = useState<{ id: number; itemQtys: Record<number, number>; legacyBags: number } | null>(null);
  const [rejecting, setRejecting] = useState<{ id: number; reason: string } | null>(null);
  const [bundling, setBundling] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [supplierRates, setSupplierRates] = useState<Record<string, number>>({});
  const [bundleForm, setBundleForm] = useState({
    supplier_id: 0,
    po_number: '',
    po_date: new Date().toISOString().slice(0, 10),
    delivery_date: '',
    delivery_location: '',
    gst_rate: 5,
  });

  const load = useCallback(async (sid: number) => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await gunnyRequestsAPI.list(sid, filter ? { status: filter } : undefined);
      setReqs(data);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    (async () => {
      try {
        const [act, lst, sups, com] = await Promise.all([
          seasonsAPI.active().catch(() => ({ data: null as any })),
          seasonsAPI.list().catch(() => ({ data: [] as any[] })),
          gunnySuppliersAPI.list().catch(() => ({ data: [] as any[] })),
          commoditiesAPI.list().catch(() => ({ data: [] as any[] })),
        ]);
        const seasonsList = Array.isArray(lst.data) ? lst.data : [];
        setSeasons(seasonsList);
        // Hide inactive masters from selection.
        setSuppliers((Array.isArray(sups.data) ? sups.data : []).filter((s: any) => s.is_active !== false));
        setCommodities((Array.isArray(com.data) ? com.data : []).filter((c: any) => c.is_active !== false));
        const activeId = act.data?.id || seasonsList.find((s: any) => s.is_active)?.id || seasonsList[0]?.id || 0;
        if (activeId) {
          setSeasonId(activeId);
          load(activeId);
        } else {
          setLoading(false);
        }
      } catch (e: any) {
        setErr(e?.response?.data?.message || 'Failed to load');
        setLoading(false);
      }
    })();
  }, [load]);

  const showFlash = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2500); };

  const saveAdjusted = async () => {
    if (!editing || !seasonId) return;
    const editReq = reqs.find((x) => x.id === editing.id);
    if (!editReq) return;
    const itemList = editReq.items && editReq.items.length > 0 ? editReq.items : null;
    try {
      const body: any = itemList
        ? { items: itemList.map((it) => ({ id: it.id, dm_approved_bags: editing.itemQtys[it.id] ?? 0 })) }
        : { dm_approved_bags: editing.legacyBags };
      const total = itemList
        ? itemList.reduce((s, it) => s + (editing.itemQtys[it.id] || 0), 0)
        : editing.legacyBags;
      await gunnyRequestsAPI.review(seasonId, editing.id, body);
      showFlash(`Request #${editing.id} approved at ${total.toLocaleString()} bags`);
      setEditing(null);
      load(seasonId);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Save failed');
    }
  };

  const reject = async () => {
    if (!rejecting || !seasonId) return;
    try {
      await gunnyRequestsAPI.review(seasonId, rejecting.id, { status: 'rejected', rejection_reason: rejecting.reason });
      showFlash(`Request #${rejecting.id} rejected`);
      setRejecting(null);
      load(seasonId);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Reject failed');
    }
  };

  const toggleSelect = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  // Open the bundle modal: pre-compute the auto values so the user sees them
  // already filled in. They're still editable.
  const openBundle = async () => {
    if (!seasonId) return;
    const selectedRows = reqs.filter((r) => selected.has(r.id));
    if (selectedRows.length === 0) return;
    const districtId = selectedRows[0].district_id;
    const poDate = new Date().toISOString().slice(0, 10);
    const deliveryLocation = selectedRows
      .map((r) => r.pacs?.name || `pacs#${r.pacs_id}`)
      .join(', ');
    let poNumber = '';
    try {
      const { data } = await gunnyRequestsAPI.poNumberPreview(seasonId, districtId, poDate);
      poNumber = data.po_number || '';
    } catch { /* fall through with blank — backend will still auto-generate */ }
    setBundleForm({
      supplier_id: 0,
      po_number: poNumber,
      po_date: poDate,
      delivery_date: '',
      delivery_location: deliveryLocation,
      gst_rate: 5,
    });
    setSupplierRates({});
    setBundling(true);
  };

  // Refetch the supplier's rate sheet whenever the picker changes inside the
  // bundle modal.
  useEffect(() => {
    if (!bundling || !bundleForm.supplier_id) {
      setSupplierRates({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await gunnySupplierRatesAPI.list(bundleForm.supplier_id);
        if (cancelled) return;
        const map: Record<string, number> = {};
        (data || []).forEach((r: any) => { if (r.is_active) map[r.bag_type] = Number(r.rate_per_bag); });
        setSupplierRates(map);
      } catch {
        if (!cancelled) setSupplierRates({});
      }
    })();
    return () => { cancelled = true; };
  }, [bundling, bundleForm.supplier_id]);

  const submitBundle = async () => {
    if (!seasonId) return;
    if (selected.size === 0) { setErr('Select at least one approved request'); return; }
    if (!bundleForm.supplier_id) { setErr('Supplier required'); return; }
    try {
      const { data: po } = await gunnyRequestsAPI.bundle(seasonId, {
        ...bundleForm,
        request_ids: Array.from(selected),
      });
      showFlash(`PO ${po.po_number} created with ${po.total_bags} bags`);
      setBundling(false);
      setSelected(new Set());
      load(seasonId);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Bundle failed');
    }
  };

  // Match against parent OR any item's commodity_id, so requests whose parent
  // rolls up to NULL (mixed-commodity) still show up when filtering by one of
  // their item commodities.
  const filteredReqs = reqs.filter((r) => {
    if (commodityFilter === 0) return true;
    if (r.commodity_id === commodityFilter) return true;
    return (r.items || []).some((it: any) => it.commodity_id === commodityFilter);
  });
  const approvedReqs = filteredReqs.filter((r) => ['dm_approved', 'dm_modified'].includes(r.status));
  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(filteredReqs, 25);
  const totalSelectedBags = reqs
    .filter((r) => selected.has(r.id))
    .reduce((s, r) => s + (r.dm_approved_bags ?? r.requested_bags), 0);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Gunny Requests</h1>
          <p className="text-sm text-gray-600">Review society requests, adjust quantities, and bundle approved ones into POs.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={seasonId || 0}
            onChange={(e) => { const v = Number(e.target.value) || 0; setSeasonId(v); if (v) load(v); else setReqs([]); }}
            className="px-3 py-1.5 text-sm border rounded bg-white">
            {seasons.length === 0 && <option value={0}>No seasons</option>}
            {seasons.map((s: any) => (
              <option key={s.id} value={s.id}>{s.season_name}{s.is_active ? ' (active)' : ''}</option>
            ))}
          </select>
          <select value={commodityFilter}
            onChange={(e) => setCommodityFilter(Number(e.target.value) || 0)}
            className="px-3 py-1.5 text-sm border rounded bg-white">
            <option value={0}>All commodities</option>
            {commodities.map((c: any) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-1.5 text-sm border rounded bg-white">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="dm_approved">Approved</option>
            <option value="dm_modified">Approved (modified)</option>
            <option value="rejected">Rejected</option>
            <option value="converted_to_po">Converted to PO</option>
          </select>
          {canManage && approvedReqs.length > 0 && (
            <button onClick={openBundle} disabled={selected.size === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded">
              <Package className="w-4 h-4" /> Bundle into PO ({selected.size})
            </button>
          )}
        </div>
      </div>

      {err && <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"><AlertCircle className="w-4 h-4" /> {err}</div>}
      {flash && <div className="mb-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm"><CheckCircle className="w-4 h-4" /> {flash}</div>}

      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading…</div>
        ) : reqs.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No requests match this filter.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-xs uppercase text-gray-600">
                {canManage && <th className="px-3 py-2 w-10"></th>}
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">PACS</th>
                <th className="px-3 py-2">District</th>
                <th className="px-3 py-2">Commodity</th>
                <th className="px-3 py-2">Items</th>
                <th className="px-3 py-2">Required by</th>
                <th className="px-3 py-2">Drop point</th>
                <th className="px-3 py-2 text-right">Requested</th>
                <th className="px-3 py-2 text-right">DM Approved</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">PO</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedItems.map((r, i) => {
                const eligibleForBundle = ['dm_approved', 'dm_modified'].includes(r.status);
                const itemRows = (r.items && r.items.length > 0) ? r.items : null;
                const sno = (page - 1) * pageSize + i + 1;
                return (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50 align-top">
                    {canManage && (
                      <td className="px-3 py-2">
                        {eligibleForBundle && (
                          <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} />
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <div className="text-gray-700">{sno}</div>
                      <div className="text-[10px] text-gray-400 font-mono">#{r.id}</div>
                    </td>
                    <td className="px-3 py-2">{r.pacs?.name || `pacs#${r.pacs_id}`}</td>
                    <td className="px-3 py-2">{r.district?.name || `d#${r.district_id}`}</td>
                    <td className="px-3 py-2 text-xs">
                      {(() => {
                        // Prefer parent rollup. If null, aggregate from items so a
                        // multi-commodity request shows "mixed (N)" instead of em-dash.
                        if (r.commodity?.name) {
                          return <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">{r.commodity.name}</span>;
                        }
                        const names = Array.from(new Set((r.items || []).map((it: any) => it.commodity?.name).filter(Boolean))) as string[];
                        if (names.length === 1) {
                          return <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded">{names[0]}</span>;
                        }
                        if (names.length > 1) {
                          return <span title={names.join(' · ')} className="inline-block px-2 py-0.5 bg-amber-50 text-amber-700 rounded">mixed ({names.length})</span>;
                        }
                        return <span className="text-gray-400">—</span>;
                      })()}
                    </td>
                    <td className="px-3 py-2 max-w-xs text-xs">
                      {itemRows ? (
                        <ul className="space-y-0.5">
                          {itemRows.map((it: any) => (
                            <li key={it.id} className="flex items-baseline gap-2">
                              {it.commodity?.name && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded">{it.commodity.name}</span>
                              )}
                              <span className="text-gray-800">{bagTypeLabel(it.bag_type)}</span>
                              {it.specification && <span className="text-gray-500">· {it.specification}</span>}
                              <span className="ml-auto text-gray-500 tabular-nums">× {Number(it.requested_bags).toLocaleString()}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-gray-700">{bagTypeLabel(r.bag_type)}{r.specification ? ` · ${r.specification}` : ''}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.required_by_date || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.delivery_godown?.godown_name || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.requested_bags.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.dm_approved_bags != null ? r.dm_approved_bags.toLocaleString() : '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                        r.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        r.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                        r.status === 'converted_to_po' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {r.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">{r.po?.po_number || '—'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canManage && (r.status === 'pending' || r.status === 'dm_modified' || r.status === 'dm_approved') && (
                        <>
                          <button onClick={() => {
                            const itemQtys: Record<number, number> = {};
                            (r.items || []).forEach((it) => { itemQtys[it.id] = it.dm_approved_bags ?? it.requested_bags; });
                            setEditing({ id: r.id, itemQtys, legacyBags: r.dm_approved_bags ?? r.requested_bags });
                          }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded mr-1">
                            <Pencil className="w-3 h-3" /> Adjust
                          </button>
                          {r.status === 'pending' && (
                            <button onClick={() => setRejecting({ id: r.id, reason: '' })}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-rose-600 hover:bg-rose-700 text-white rounded">
                              <X className="w-3 h-3" /> Reject
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
        {!loading && reqs.length > 0 && (
          <div className="px-3 py-3 border-t bg-gray-50">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="requests"
            />
          </div>
        )}
      </div>

      {editing && (() => {
        const editReq = reqs.find((x) => x.id === editing.id);
        if (!editReq) return null;
        const requestedTotal = editReq.requested_bags;
        const editItems = (editReq.items && editReq.items.length > 0) ? editReq.items : null;
        const approvedTotal = editItems
          ? editItems.reduce((s, it) => s + (editing.itemQtys[it.id] || 0), 0)
          : editing.legacyBags;
        const delta = approvedTotal - requestedTotal;
        const pct = requestedTotal > 0 ? (delta / requestedTotal) * 100 : 0;

        const setItemQty = (itemId: number, q: number) => setEditing({
          ...editing,
          itemQtys: { ...editing.itemQtys, [itemId]: Math.max(0, Math.round(q || 0)) },
        });
        const applyToAll = (factor: number) => {
          if (!editItems) {
            setEditing({ ...editing, legacyBags: Math.max(0, Math.round(requestedTotal * factor)) });
            return;
          }
          const next: Record<number, number> = {};
          editItems.forEach((it) => { next[it.id] = Math.max(0, Math.round(it.requested_bags * factor)); });
          setEditing({ ...editing, itemQtys: next });
        };

        return (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">
              <div className="px-6 py-4 border-b">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-semibold text-gray-900">Adjust request #{editReq.id}</h3>
                  <span className="text-xs text-gray-500">{editReq.pacs?.name || `pacs#${editReq.pacs_id}`} · {editReq.district?.name || `d#${editReq.district_id}`}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                  <span><span className="text-gray-400">Required by:</span> <span className="text-gray-800">{editReq.required_by_date || '—'}</span></span>
                  <span><span className="text-gray-400">Drop point:</span> <span className="text-gray-800">{editReq.delivery_godown?.godown_name || '—'}</span></span>
                </div>
              </div>

              <div className="px-6 py-5">
                <div className="flex items-baseline justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Approve per line item</label>
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" onClick={() => applyToAll(1)}
                      className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded border border-emerald-200">
                      As requested
                    </button>
                    <button type="button" onClick={() => applyToAll(0.9)}
                      className="px-2 py-1 text-xs bg-gray-50 text-gray-700 hover:bg-gray-100 rounded border border-gray-200">
                      −10%
                    </button>
                    <button type="button" onClick={() => applyToAll(0.75)}
                      className="px-2 py-1 text-xs bg-gray-50 text-gray-700 hover:bg-gray-100 rounded border border-gray-200">
                      −25%
                    </button>
                    <button type="button" onClick={() => applyToAll(0.5)}
                      className="px-2 py-1 text-xs bg-gray-50 text-gray-700 hover:bg-gray-100 rounded border border-gray-200">
                      −50%
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr className="text-left text-xs uppercase text-gray-600">
                        <th className="px-3 py-2">Bag type</th>
                        <th className="px-3 py-2 text-right">Requested</th>
                        <th className="px-3 py-2 text-right w-36">Approved</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editItems ? editItems.map((it) => {
                        const qty = editing.itemQtys[it.id] ?? 0;
                        const itemDelta = qty - it.requested_bags;
                        return (
                          <tr key={it.id} className="border-b last:border-0 align-middle">
                            <td className="px-3 py-2">
                              <div className="text-gray-800">{bagTypeLabel(it.bag_type)}</div>
                              {it.specification && <div className="text-xs text-gray-500">{it.specification}</div>}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                              {Number(it.requested_bags).toLocaleString()}
                            </td>
                            <td className="px-3 py-2">
                              <input type="number" min={0} value={qty || ''}
                                onChange={(e) => setItemQty(it.id, Number(e.target.value) || 0)}
                                className={`w-full px-2 py-1.5 border-2 rounded text-sm font-semibold tabular-nums text-right focus:outline-none ${
                                  itemDelta === 0 ? 'border-gray-200 focus:border-blue-500' :
                                  itemDelta < 0 ? 'border-amber-300 focus:border-amber-500 bg-amber-50' :
                                  'border-blue-300 focus:border-blue-500 bg-blue-50'
                                }`} />
                            </td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td className="px-3 py-2">
                            <div className="text-gray-800">{bagTypeLabel(editReq.bag_type)}</div>
                            {editReq.specification && <div className="text-xs text-gray-500">{editReq.specification}</div>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                            {Number(requestedTotal).toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" min={0} value={editing.legacyBags || ''}
                              onChange={(e) => setEditing({ ...editing, legacyBags: Number(e.target.value) || 0 })}
                              className="w-full px-2 py-1.5 border-2 border-gray-200 rounded text-sm font-semibold tabular-nums text-right focus:outline-none focus:border-blue-500" />
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                      <tr>
                        <td className="px-3 py-2 font-semibold text-gray-800">Total</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-700">
                          {Number(requestedTotal).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">
                          {approvedTotal.toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {approvedTotal > 0 && delta !== 0 && (
                  <div className={`mt-3 text-sm flex items-baseline gap-2 ${delta < 0 ? 'text-amber-700' : 'text-blue-700'}`}>
                    <span className="font-medium">
                      {delta < 0 ? `Reducing by ${Math.abs(delta).toLocaleString()} bags` : `Increasing by ${delta.toLocaleString()} bags`}
                    </span>
                    <span className="text-xs tabular-nums">({pct > 0 ? '+' : ''}{pct.toFixed(1)}%)</span>
                  </div>
                )}
                {approvedTotal > 0 && delta === 0 && (
                  <div className="mt-3 text-sm text-emerald-700 font-medium">Approving as requested</div>
                )}

                {editReq.notes && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-gray-700">
                    <span className="text-gray-500">Notes from PACS:</span> {editReq.notes}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Cancel</button>
                <button onClick={saveAdjusted} disabled={approvedTotal <= 0}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50">
                  Approve at {approvedTotal.toLocaleString()}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {rejecting && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="px-6 py-4 border-b"><h3 className="font-semibold">Reject request #{rejecting.id}</h3></div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <textarea rows={3} value={rejecting.reason}
                onChange={(e) => setRejecting({ ...rejecting, reason: e.target.value })}
                className="w-full px-3 py-2 border rounded text-sm" placeholder="e.g. Quota already exhausted for this season" />
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
              <button onClick={() => setRejecting(null)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Cancel</button>
              <button onClick={reject} disabled={!rejecting.reason}
                className="px-4 py-2 text-sm bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded">Reject</button>
            </div>
          </div>
        </div>
      )}

      {bundling && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="font-semibold">Bundle into PO</h3>
              <p className="text-xs text-gray-500 mt-0.5">{selected.size} requests · total {totalSelectedBags.toLocaleString()} bags</p>
            </div>
            <div className="px-6 py-4 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                <select value={bundleForm.supplier_id} onChange={(e) => setBundleForm({ ...bundleForm, supplier_id: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded text-sm bg-white">
                  <option value={0}>Select supplier…</option>
                  {suppliers.map((s) => (<option key={s.id} value={s.id}>{s.supplier_name}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                <input value={bundleForm.po_number} onChange={(e) => setBundleForm({ ...bundleForm, po_number: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm font-mono" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO Date</label>
                <input type="date" value={bundleForm.po_date} onChange={(e) => setBundleForm({ ...bundleForm, po_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Date</label>
                <input type="date" value={bundleForm.delivery_date} onChange={(e) => setBundleForm({ ...bundleForm, delivery_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Location</label>
                <input value={bundleForm.delivery_location} onChange={(e) => setBundleForm({ ...bundleForm, delivery_location: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GST %</label>
                <input type="number" step="0.01" min={0} value={bundleForm.gst_rate}
                  onChange={(e) => setBundleForm({ ...bundleForm, gst_rate: Number(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>

            {bundleForm.supplier_id > 0 && (() => {
              // Aggregate selected requests into per-bag-type quantities so DM
              // sees the resolved rate × qty preview before committing.
              const selectedRows = reqs.filter((r) => selected.has(r.id));
              const qtyByType: Record<string, number> = {};
              for (const r of selectedRows) {
                const items = (r.items && r.items.length > 0) ? r.items : null;
                if (items) {
                  for (const it of items) {
                    const q = it.dm_approved_bags ?? it.requested_bags;
                    qtyByType[it.bag_type] = (qtyByType[it.bag_type] || 0) + q;
                  }
                } else if (r.bag_type) {
                  const q = r.dm_approved_bags ?? r.requested_bags;
                  qtyByType[r.bag_type] = (qtyByType[r.bag_type] || 0) + q;
                }
              }
              const types = Object.keys(qtyByType).sort();
              const missing = types.filter((t) => !(t in supplierRates));
              const subtotal = types.reduce((s, t) => s + (supplierRates[t] || 0) * qtyByType[t], 0);
              const gst = +(subtotal * (bundleForm.gst_rate || 0) / 100).toFixed(2);
              const total = +(subtotal + gst).toFixed(2);
              return (
                <div className="px-6 pb-4">
                  <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Rate sheet (from supplier master)</div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr className="text-left text-xs uppercase text-gray-600">
                          <th className="px-3 py-2">Bag type</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="px-3 py-2 text-right">Rate (₹)</th>
                          <th className="px-3 py-2 text-right">Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {types.map((t) => {
                          const rate = supplierRates[t];
                          const qty = qtyByType[t];
                          return (
                            <tr key={t} className="border-b last:border-0">
                              <td className="px-3 py-2 text-gray-800">{bagTypeLabel(t)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{qty.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {rate != null ? rate.toFixed(2) : <span className="text-rose-600 font-medium">missing</span>}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {rate != null ? (rate * qty).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {missing.length === 0 && (
                        <tfoot className="bg-gray-50 border-t">
                          <tr>
                            <td colSpan={3} className="px-3 py-1.5 text-right text-gray-600">Subtotal</td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-medium">{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                          <tr>
                            <td colSpan={3} className="px-3 py-1.5 text-right text-gray-600">GST {bundleForm.gst_rate}%</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{gst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                          <tr className="border-t-2 border-gray-300">
                            <td colSpan={3} className="px-3 py-2 text-right font-semibold text-gray-800">Total</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">{total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                  {missing.length > 0 && (
                    <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700">
                      No active rate for: <span className="font-semibold">{missing.map(bagTypeLabel).join(', ')}</span>.
                      Add the missing rate(s) to the supplier rate master, then re-open this dialog.
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
              <button onClick={() => setBundling(false)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded">Cancel</button>
              <button onClick={submitBundle}
                disabled={!bundleForm.supplier_id || (() => {
                  const selectedRows = reqs.filter((r) => selected.has(r.id));
                  const types = new Set<string>();
                  for (const r of selectedRows) {
                    const items = (r.items && r.items.length > 0) ? r.items : null;
                    if (items) items.forEach((it) => types.add(it.bag_type));
                    else if (r.bag_type) types.add(r.bag_type);
                  }
                  return Array.from(types).some((t) => !(t in supplierRates));
                })()}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50">
                Create PO
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GunnyRequestsManagement;
